import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ChartLine, Clock, CurrencyJpy, WarningCircle } from '@phosphor-icons/react';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/enterprise/EmptyState';
import { StatusBadge } from '@/components/enterprise/StatusBadge';
import { devApi, STAGE_TONE } from './devApi';
import { Trends } from './Trends';
import { jstDateTime } from '@/utils/jstDate';

const yen = (v) => `¥${Math.round(v || 0).toLocaleString('ja-JP')}`;
const dnum = (v) => (v === null || v === undefined ? '—' : `${v}日`);
const pct = (v) => (v === null || v === undefined ? '—' : `${v}%`);

const Card = ({ title, hint, children, testid }) => (
  <section className="rounded-lg border border-zinc-300 bg-white p-4 shadow-sm" data-testid={testid}>
    <p className="text-sm font-bold text-zinc-900">{title}</p>
    {hint && <p className="text-xs text-zinc-600">{hint}</p>}
    <div className="mt-3">{children}</div>
  </section>
);

const Tile = ({ label, value, hint, tone = 'text-zinc-900', testid }) => (
  <div className="rounded-lg border border-zinc-300 bg-white px-4 py-3 shadow-sm" data-testid={testid}>
    <p className="text-xs font-semibold text-zinc-600">{label}</p>
    <p className={`text-2xl font-bold tabular-nums ${tone}`}>{value}</p>
    {hint && <p className="text-xs text-zinc-500">{hint}</p>}
  </div>
);

/** 経営向けの全体傾向 — 全案件のリードタイム・原価・つまずきを一枚で見る。 */
export default function Analytics() {
  const nav = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    devApi.analytics().then(setD).catch(() => toast.error('全体傾向を読み込めませんでした'));
  }, []);

  if (!d) {
    return <div className="p-6 text-sm text-zinc-600" data-testid="dev-analytics-loading">読み込み中…</div>;
  }
  const t = d.totals;
  const maxStage = Math.max(...d.stages.map((s) => s.avg_days || 0), 1);

  return (
    <div className="space-y-6" data-testid="dev-analytics-page">
      <PageHeader title="開発の全体傾向"
        description="構想から完了までに蓄えたデータで、どこに時間とお金がかかっているかを見ます。"
        testid="dev-analytics-header"
        secondary={
          <>
            <Button variant="outline" size="sm" onClick={() => nav('/engineering/developments/learning')}
              data-testid="dev-analytics-learning-btn">学びのダイジェスト</Button>
            <Button variant="outline" size="sm" onClick={() => nav('/engineering/developments/insights')}
              data-testid="dev-analytics-insights-btn">トラブル分析</Button>
            <Button variant="outline" size="sm" onClick={() => nav('/engineering/developments/readiness')}
              data-testid="dev-analytics-readiness-btn">試作の準備</Button>
          </>
        } />

      {t.projects === 0 ? (
        <EmptyState icon={ChartLine} title="集計できる開発案件がありません"
          description="開発案件を登録すると、ここに横断的な分析が表示されます。"
          testid="dev-analytics-empty" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Tile label="開発案件" value={t.projects} hint={`進行中 ${t.active} / 完了 ${t.completed}`}
              testid="dev-analytics-tile-projects" />
            <Tile label="平均リードタイム" value={dnum(t.avg_lead_time_days)}
              hint={`進行中の平均経過 ${dnum(t.avg_active_age_days)}`}
              testid="dev-analytics-tile-leadtime" />
            <Tile label="一次合格率" value={pct(t.first_pass_rate)}
              hint={`試作 ${t.prototypes}件 · 改訂 ${t.revisions}回`}
              tone={t.first_pass_rate !== null && t.first_pass_rate < 50 ? 'text-rose-700' : 'text-emerald-700'}
              testid="dev-analytics-tile-firstpass" />
            <Tile label="開発原価の合計" value={yen(t.final_cost)}
              hint={`材料 ${yen(t.material_cost)} / スクラップ ${yen(t.scrap_cost)}`}
              testid="dev-analytics-tile-cost" />
          </div>

          {!!t.unknown_unit_costs && (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
              data-testid="dev-analytics-cost-note">
              単価が未登録の部品行が {t.unknown_unit_costs} 件あります。製造品目の原価・技術資産の単価、
              または部品行の単価を入力すると原価に反映されます。
            </p>
          )}

          <div className="grid gap-3 lg:grid-cols-2">
            <Card title="ステージ別の滞留" hint="1回あたりの平均日数（過去の通過実績から）"
              testid="dev-analytics-stages">
              <div className="space-y-2">
                {d.stages.map((s) => (
                  <div key={s.stage} data-testid={`dev-analytics-stage-${s.stage}`}>
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-sm text-zinc-900">{s.label}
                        <span className="ml-1 text-xs text-zinc-500">進行中 {s.active}件</span>
                      </p>
                      <p className="shrink-0 text-sm font-bold tabular-nums text-zinc-900">
                        {dnum(s.avg_days)}</p>
                    </div>
                    <div className="h-1.5 w-full rounded bg-zinc-200">
                      <div className="h-1.5 rounded bg-teal-600"
                        style={{ width: `${Math.max(((s.avg_days || 0) / maxStage) * 100, 2)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card title="種類別のサマリー" hint="どの種類の開発に時間と費用がかかっているか"
              testid="dev-analytics-types">
              <div className="divide-y divide-zinc-200">
                {d.by_type.map((r) => (
                  <div key={r.label} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-2"
                    data-testid={`dev-analytics-type-${r.label}`}>
                    <p className="min-w-0 flex-1 truncate text-sm font-semibold text-zinc-900">{r.label}</p>
                    <p className="text-xs text-zinc-600">{r.count}件（完了 {r.completed}）</p>
                    <p className="text-xs text-zinc-600">平均 {dnum(r.avg_lead_time_days)}</p>
                    <p className="text-sm font-bold tabular-nums text-zinc-900">{yen(r.final_cost)}</p>
                  </div>
                ))}
              </div>
            </Card>

            <Card title="原価が大きい案件" hint="最終原価（投入 − 返却）の上位"
              testid="dev-analytics-topcost">
              <div className="divide-y divide-zinc-200">
                {d.top_cost.map((r) => (
                  <button key={r.development_id} type="button"
                    onClick={() => nav(`/engineering/developments/${r.development_id}`)}
                    data-testid={`dev-analytics-cost-${r.development_id}`}
                    className="flex w-full items-center gap-2 py-2 text-left hover:bg-teal-50">
                    <CurrencyJpy size={14} className="text-teal-700" />
                    <span className="min-w-0 flex-1 truncate text-sm">{r.title}</span>
                    <span className="text-xs text-zinc-600">材料 {yen(r.material_cost)}</span>
                    <span className="text-sm font-bold tabular-nums">{yen(r.final_cost)}</span>
                  </button>
                ))}
                {d.top_cost.length === 0 && <p className="text-sm text-zinc-500">記録はありません。</p>}
              </div>
            </Card>

            <Card title="止まっている案件" hint="現在のステージに長くとどまっている順"
              testid="dev-analytics-slowest">
              <div className="divide-y divide-zinc-200">
                {d.slowest.map((r) => (
                  <button key={r.development_id} type="button"
                    onClick={() => nav(`/engineering/developments/${r.development_id}`)}
                    data-testid={`dev-analytics-slow-${r.development_id}`}
                    className="flex w-full items-center gap-2 py-2 text-left hover:bg-teal-50">
                    <Clock size={14} className="text-amber-700" />
                    <span className="min-w-0 flex-1 truncate text-sm">{r.title}</span>
                    <StatusBadge tone={STAGE_TONE[r.current_stage] || 'neutral'}>
                      {r.current_stage_label}</StatusBadge>
                    <span className="text-sm font-bold tabular-nums">{dnum(r.days_in_stage)}</span>
                  </button>
                ))}
                {d.slowest.length === 0 && <p className="text-sm text-zinc-500">記録はありません。</p>}
              </div>
            </Card>
          </div>

          <Card title="案件ごとの明細" hint="クリックで案件を開きます" testid="dev-analytics-rows">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[880px] text-sm">
                <thead>
                  <tr className="border-b border-zinc-300 text-left text-xs text-zinc-600">
                    <th className="py-2 pr-3 font-semibold">案件</th>
                    <th className="py-2 pr-3 font-semibold">ステージ</th>
                    <th className="py-2 pr-3 text-right font-semibold">経過</th>
                    <th className="py-2 pr-3 text-right font-semibold">試作</th>
                    <th className="py-2 pr-3 text-right font-semibold">不具合</th>
                    <th className="py-2 pr-3 text-right font-semibold">改訂</th>
                    <th className="py-2 pr-3 text-right font-semibold">材料費</th>
                    <th className="py-2 pr-3 text-right font-semibold">スクラップ</th>
                    <th className="py-2 text-right font-semibold">最終原価</th>
                  </tr>
                </thead>
                <tbody>
                  {d.rows.map((r) => (
                    <tr key={r.development_id} onClick={() => nav(`/engineering/developments/${r.development_id}`)}
                      data-testid={`dev-analytics-row-${r.development_id}`}
                      className="cursor-pointer border-b border-zinc-100 hover:bg-teal-50">
                      <td className="py-2 pr-3">
                        <p className="max-w-[240px] truncate font-semibold text-zinc-900">{r.title}</p>
                        <p className="text-xs text-zinc-600">{r.development_type_label}</p>
                      </td>
                      <td className="py-2 pr-3">
                        <StatusBadge tone={STAGE_TONE[r.current_stage] || 'neutral'}>
                          {r.current_stage_label}</StatusBadge>
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">{dnum(r.lead_time_days)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{r.prototypes}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {r.failures ? <span className="font-bold text-rose-700">{r.failures}</span> : 0}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">{r.revisions}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{yen(r.material_cost)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {r.scrap_cost ? <span className="text-amber-800">{yen(r.scrap_cost)}</span> : '—'}
                      </td>
                      <td className="py-2 text-right font-bold tabular-nums">{yen(r.final_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {d.top_scrap.length > 0 && (            <Card title="スクラップ費が出ている案件" hint="廃棄した部品の金額" testid="dev-analytics-topscrap">
              <div className="flex flex-wrap gap-2">
                {d.top_scrap.map((r) => (
                  <button key={r.development_id} type="button"
                    onClick={() => nav(`/engineering/developments/${r.development_id}`)}
                    data-testid={`dev-analytics-scrap-${r.development_id}`}
                    className="flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs text-amber-900 hover:bg-amber-100">
                    <WarningCircle size={12} />
                    <span className="max-w-[180px] truncate">{r.title}</span>
                    <span className="font-bold tabular-nums">{yen(r.scrap_cost)}</span>
                  </button>
                ))}
              </div>
            </Card>
          )}
          <Trends />
        </>
      )}
      <p className="text-xs text-zinc-500">作成: {jstDateTime(d.generated_at)}</p>
    </div>
  );
}
